// Эндпоинты организационного контура: юрлицо → заказчик → договор → объект → пост,
// плюс норматив часов и укомплектованность.
//
// Формы — строго по contracts/openapi/bark-core.v1.yaml. Контракт правит только сессия ядра,
// здесь его зеркало, а не вольный пересказ: поле, придуманное на фронте, даёт зелёную сборку
// и пустой экран.
//
// Часы ходят СТРОКОЙ, не числом (ПРАВИЛА.md, раздел 3): «63.5» в JSON, а не 63.5. Число с
// плавающей точкой теряет десятую долю часа на сумме по постам, а из этой суммы считается
// норматив, из норматива — ставка, из ставки — зарплата.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { DecimalString, ListResponse } from '@/shared/api/types';

// ── Справочники ───────────────────────────────────────────────────────────────────────────

/**
 * Юрлицо со всеми реквизитами.
 *
 * Реквизиты нужны не карточке, а БУМАГЕ: тз (раздел 5.5) требует их в шапке выгрузки и в
 * печатной форме ведомости, которую охранник подписывает при получении денег. До 03.09.2026
 * наружу отдавались только имя и ИНН, а остальное лежало в базе и заполнить его было нечем.
 */
export interface LegalEntityOut {
  id: string;
  name: string;
  short_name: string | null;
  inn: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  address?: string | null;
  /** Банковские реквизиты одной строкой, как их пишут в шапке: счёт, банк, БИК, корсчёт. */
  bank_details?: string | null;
  signer_name?: string | null;
  signer_position?: string | null;
  /** На основании чего подписывает: устав, доверенность номер такой-то. */
  signer_basis?: string | null;
  is_active: boolean;
}

export interface LegalEntityPatch {
  name?: string | null;
  short_name?: string | null;
  inn?: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  address?: string | null;
  bank_details?: string | null;
  signer_name?: string | null;
  signer_position?: string | null;
  signer_basis?: string | null;
}

/** Заведение юрлица. Обязательно только название — остальное дописывается правкой. */
export interface LegalEntityIn {
  name: string;
  short_name?: string | null;
  inn?: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  address?: string | null;
  bank_details?: string | null;
  signer_name?: string | null;
  signer_position?: string | null;
  signer_basis?: string | null;
}

export interface SiteTypeOut {
  id: string;
  code: string;
  title: string;
  /** Шаблон ведомости типа: объект наследует его, если своего не назначено. */
  payroll_template_id: string | null;
  is_active: boolean;
}

export interface EmploymentTypeOut {
  id: string;
  code: string;
  title: string;
  is_active: boolean;
}

// ── Заказчики и договоры ──────────────────────────────────────────────────────────────────

export interface CustomerOut {
  id: string;
  name: string;
  inn?: string | null;
  industry?: string | null;
  region?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  is_active: boolean;
}

export interface CustomerIn {
  name: string;
  inn?: string | null;
  industry?: string | null;
  region?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
}

/**
 * Правка заказчика. Опечатка в ИНН или телефоне до 15.09.2026 не чинилась ничем: у заказчика
 * был только `POST`, и единственным выходом было завести его заново — то есть оставить в
 * справочнике двойника, к которому привязана половина договоров.
 *
 * `null` означает «убрать значение», как и у объекта: контактное лицо уволилось, и старое имя
 * врёт хуже, чем его отсутствие. «Не трогать» — это не прислать поле вовсе.
 */
export interface CustomerPatch {
  name?: string;
  inn?: string | null;
  industry?: string | null;
  region?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  /** Заказчик не удаляется: на нём висят договоры и объекты с историей. */
  is_active?: boolean;
}

export interface ContractOut {
  id: string;
  number: string | null;
  customer_id: string;
  customer_name: string;
  legal_entity_id: string;
  legal_entity_name: string;
  /** Дата подписания, ISO «2026-07-21». */
  signed_at: string | null;
  date_from: string | null;
  date_to: string | null;
  status: string;
  /**
   * Состояние словом. Показывать человеку надо ЕГО, а не код: карточка объекта печатала
   * «состояние договора: active». Своего словаря в вебе нет намеренно — он разошёлся бы с
   * сервером на первом же новом состоянии.
   */
  status_title?: string;
  is_active: boolean;
  /** Объекты, привязанные к договору. */
  site_ids?: string[];
}

export interface ContractIn {
  customer_id: string;
  legal_entity_id: string;
  number?: string | null;
  signed_at?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

/**
 * Правка договора: номер, даты, юрлицо-исполнитель.
 *
 * Заказчика тут нет намеренно. К договору привязаны объекты, а через них ведомости: перевод
 * договора другому заказчику это не правка поля, а разговор с бумагами. То же решение, что у
 * объекта.
 */
export interface ContractPatch {
  number?: string | null;
  signed_at?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  legal_entity_id?: string;
  is_active?: boolean;
}

// ── Объекты и посты ───────────────────────────────────────────────────────────────────────

export interface SiteOut {
  id: string;
  name: string;
  city: string | null;
  region: string | null;
  /** Куда ехать заступающему. До 06.09.2026 адрес принимался, но обратно не отдавался. */
  address: string | null;
  /** Пояс объекта в форме IANA: смена целиком относится к дате заступления по нему. */
  timezone: string;
  /**
   * Точка объекта СТРОКОЙ, как и все дробные величины проекта.
   *
   * Число в json браузер разбирает как приближение с плавающей точкой, а шестой знак широты —
   * это десять сантиметров. Пусто означает «зоны у объекта нет»: отметка на посту принимается
   * без сверки места, и это законное состояние, а не недоделка.
   */
  lat: string | null;
  lon: string | null;
  /** Радиус зоны в метрах: насколько далеко от точки человек ещё считается «на месте». */
  geofence_radius_m: number;
  /**
   * Область объекта фигурой. ⚠️ Заданная фигура ОТМЕНЯЕТ круг, а не дополняет его: круг есть
   * у каждого объекта всегда (умолчание 200 метров с первой миграции), и считай мы обе формы,
   * точка в вырезе площадки всё равно попадала бы в круг.
   */
  geofence_polygon: { lat: string; lon: string }[] | null;
  /** Через сколько часов на объекте ждут обход. Пусто — обходов здесь не ждут. */
  patrol_interval_hours: number | null;
  /** Старший объекта: от него считается изоляция, ему приходят уведомления по объекту. */
  field_manager_employee_id: string | null;
  field_manager_name: string | null;
  is_active: boolean;
  customer_id: string;
  customer_name: string;
  legal_entity_id: string;
  contract_id: string | null;
  site_type_id: string | null;
  site_type_title: string | null;
  /**
   * Шаблон, заданный ИМЕННО этому объекту. Пусто — наследует от типа.
   *
   * Отдельно от действующего затем, чтобы экран настройки различал «задан здесь» и
   * «унаследован»: у второго правка состава колонок поедет у всех соседей по типу.
   */
  own_payroll_template_id: string | null;
  /** Шаблон ведомости: свой у объекта либо унаследованный от типа. */
  effective_payroll_template_id: string | null;
}

export interface SiteIn {
  name: string;
  customer_id: string;
  legal_entity_id: string;
  address?: string | null;
  city?: string | null;
  region?: string | null;
  contract_id?: string | null;
  site_type_id?: string | null;
  timezone?: string;
  /**
   * То, что человек скопировал с карты: пара чисел либо ссылка «Яндекс Карт», 2ГИС, Google.
   *
   * ОДНО поле вместо «широты» и «долготы» намеренно. Разбирает его сервер
   * (`server/app/domain/geo/place.py`): правило одно на кабинет, приложение и перенос данных,
   * а второй разбор здесь разошёлся бы с первым на первом же новом виде ссылки.
   */
  map_point?: string | null;
  geofence_radius_m?: number;
  geofence_polygon?: { lat: string; lon: string }[] | null;
  patrol_interval_hours?: number | null;
  field_manager_employee_id?: string | null;
}

/** Частичная правка объекта. Закрытие объекта — `is_active: false` (сценарий 16). */
export interface SitePatch {
  name?: string | null;
  address?: string | null;
  city?: string | null;
  region?: string | null;
  contract_id?: string | null;
  site_type_id?: string | null;
  timezone?: string | null;
  /** Пустая строка означает «убрать точку». Не трогать — не присылать поле вовсе. */
  map_point?: string | null;
  geofence_radius_m?: number | null;
  /** Пустой список означает «убрать фигуру и вернуться к кругу». */
  geofence_polygon?: { lat: string; lon: string }[] | null;
  patrol_interval_hours?: number | null;
  field_manager_employee_id?: string | null;
  is_active?: boolean | null;
}

export interface PostOut {
  id: string;
  site_id: string;
  name: string;
  /** Часов в сутки строкой: «12», «18», «24», встречается «63.5». */
  hours_per_day: string;
  required_guard_category: number | null;
  schedule_type: string | null;
  /** Режим работы по-русски. Пусто — режим не задан, и смены по такому посту не строятся. */
  schedule_type_title?: string | null;
  simultaneous_guards: number;
  is_active: boolean;
}

/** Режим работы поста из справочника: по нему строится график смен (сценарий 17). */
export interface ScheduleTypeOut {
  code: string;
  title: string;
}

export function listScheduleTypes(signal?: AbortSignal): Promise<ListResponse<ScheduleTypeOut>> {
  return request<ListResponse<ScheduleTypeOut>>('/api/v1/post-schedule-types', { signal });
}

export interface PostIn {
  name: string;
  /** Часы принимает только через decimalString(): «24», «63.5». Число сюда не пролезает. */
  hours_per_day: DecimalString;
  required_guard_category?: number | null;
  schedule_type?: string | null;
  simultaneous_guards?: number;
}

/**
 * Правка поста.
 *
 * ⚠️ Часы в сутки и число охранников — это НЕ подписи на карточке. Из часов по активным постам
 * складывается расчётный норматив объекта, а из норматива считается ставка человека; из числа
 * охранников — требуемая укомплектованность. Часы принимает только `decimalString()`: число
 * сюда не пролезает.
 */
export interface PostPatch {
  name?: string;
  hours_per_day?: DecimalString;
  required_guard_category?: number | null;
  schedule_type?: string | null;
  simultaneous_guards?: number;
  /** Снятие поста. Недействующий выпадает из норматива и из укомплектованности. */
  is_active?: boolean;
}

// ── Норматив и укомплектованность ─────────────────────────────────────────────────────────

/** Норматив объекта за месяц. Все величины строками — это часы, не деньги. */
export interface NormOut {
  site_id: string;
  year: number;
  month: number;
  days: number;
  /** Расчёт: сумма часов в сутки по активным постам × дни месяца. */
  computed: string;
  /** Ручное значение на месяц, если задано. */
  override: string | null;
  /** Действующее: ручное поверх расчётного. */
  effective: string;
}

export interface NormOverrideIn {
  /** Ручной норматив на месяц строкой; null снимает переопределение. */
  hours: DecimalString | null;
}

export interface StaffingOut {
  site_id: string;
  /** Сумма одновременных охранников по активным постам. */
  required: number;
  /** Людей с живым назначением на объект сегодня. */
  assigned: number;
}

// ── Вызовы ────────────────────────────────────────────────────────────────────────────────

export function listLegalEntities(signal?: AbortSignal): Promise<ListResponse<LegalEntityOut>> {
  return request<ListResponse<LegalEntityOut>>('/api/v1/legal-entities', { signal });
}

/**
 * Завести юрлицо. Обязательно одно название: остальное заказчик берёт из устава и найдёт его
 * не сразу, а работать надо сегодня. Недостающее дописывается правкой и видно на экране.
 */
export function createLegalEntity(body: LegalEntityIn): Promise<LegalEntityOut> {
  return request<LegalEntityOut>('/api/v1/legal-entities', { method: 'POST', body });
}

export function patchLegalEntity(
  legalEntityId: string,
  body: LegalEntityPatch,
): Promise<LegalEntityOut> {
  return request<LegalEntityOut>(`/api/v1/legal-entities/${encodeURIComponent(legalEntityId)}`, {
    method: 'PATCH',
    body,
  });
}

export function listSiteTypes(signal?: AbortSignal): Promise<ListResponse<SiteTypeOut>> {
  return request<ListResponse<SiteTypeOut>>('/api/v1/site-types', { signal });
}

export interface EmploymentTypeIn {
  title: string;
  /** Машинный ключ. Не заполняем: сервер выводит его из названия сам. */
  code?: string;
  sort_order?: number;
}

export interface EmploymentTypePatch {
  title?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface SiteTypeIn {
  title: string;
  code?: string;
  payroll_template_id?: string | null;
  sort_order?: number;
}

export interface SiteTypePatch {
  title?: string;
  payroll_template_id?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export function createEmploymentType(body: EmploymentTypeIn): Promise<EmploymentTypeOut> {
  return request<EmploymentTypeOut>('/api/v1/employment-types', { method: 'POST', body });
}

export function updateEmploymentType(
  typeId: string,
  body: EmploymentTypePatch,
): Promise<EmploymentTypeOut> {
  return request<EmploymentTypeOut>(`/api/v1/employment-types/${typeId}`, {
    method: 'PATCH',
    body,
  });
}

export function createSiteType(body: SiteTypeIn): Promise<SiteTypeOut> {
  return request<SiteTypeOut>('/api/v1/site-types', { method: 'POST', body });
}

export function updateSiteType(typeId: string, body: SiteTypePatch): Promise<SiteTypeOut> {
  return request<SiteTypeOut>(`/api/v1/site-types/${typeId}`, { method: 'PATCH', body });
}

export function listEmploymentTypes(
  signal?: AbortSignal,
): Promise<ListResponse<EmploymentTypeOut>> {
  return request<ListResponse<EmploymentTypeOut>>('/api/v1/employment-types', { signal });
}

export interface CustomersQuery {
  /** Поиск по названию. */
  q?: string;
}

export function listCustomers(
  params: CustomersQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<CustomerOut>> {
  return request<ListResponse<CustomerOut>>(`/api/v1/customers${queryString({ q: params.q })}`, {
    signal,
  });
}

export function createCustomer(body: CustomerIn, idempotencyKey?: string): Promise<CustomerOut> {
  return request<CustomerOut>('/api/v1/customers', { method: 'POST', body, idempotencyKey });
}

/** Правка заказчика. Закрытие — `{ is_active: false }`: записи не удаляются. */
export function patchCustomer(customerId: string, body: CustomerPatch): Promise<CustomerOut> {
  return request<CustomerOut>(`/api/v1/customers/${encodeURIComponent(customerId)}`, {
    method: 'PATCH',
    body,
  });
}

export interface ContractsQuery {
  customer_id?: string;
}

export function listContracts(
  params: ContractsQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<ContractOut>> {
  return request<ListResponse<ContractOut>>(
    `/api/v1/contracts${queryString({ customer_id: params.customer_id })}`,
    { signal },
  );
}

export function createContract(body: ContractIn, idempotencyKey?: string): Promise<ContractOut> {
  return request<ContractOut>('/api/v1/contracts', { method: 'POST', body, idempotencyKey });
}

/** Правка договора. Заказчик не меняется: перевод договора — разговор с бумагами. */
export function patchContract(contractId: string, body: ContractPatch): Promise<ContractOut> {
  return request<ContractOut>(`/api/v1/contracts/${encodeURIComponent(contractId)}`, {
    method: 'PATCH',
    body,
  });
}

export interface SitesQuery {
  /** true — только действующие. */
  active?: boolean;
  customer_id?: string;
  /** Поиск по названию. */
  q?: string;
}

export function listSites(
  params: SitesQuery = {},
  signal?: AbortSignal,
): Promise<ListResponse<SiteOut>> {
  return request<ListResponse<SiteOut>>(
    `/api/v1/sites${queryString({
      active: params.active,
      customer_id: params.customer_id,
      q: params.q,
    })}`,
    { signal },
  );
}

export function createSite(body: SiteIn, idempotencyKey?: string): Promise<SiteOut> {
  return request<SiteOut>('/api/v1/sites', { method: 'POST', body, idempotencyKey });
}

export function getSite(siteId: string, signal?: AbortSignal): Promise<SiteOut> {
  return request<SiteOut>(`/api/v1/sites/${encodeURIComponent(siteId)}`, { signal });
}

/** Правка объекта. Закрытие — `{ is_active: false }`: записи не удаляются, история остаётся. */
export function patchSite(siteId: string, body: SitePatch): Promise<SiteOut> {
  return request<SiteOut>(`/api/v1/sites/${encodeURIComponent(siteId)}`, {
    method: 'PATCH',
    body,
  });
}

export function listPosts(siteId: string, signal?: AbortSignal): Promise<ListResponse<PostOut>> {
  return request<ListResponse<PostOut>>(`/api/v1/sites/${encodeURIComponent(siteId)}/posts`, {
    signal,
  });
}

export function createPost(
  siteId: string,
  body: PostIn,
  idempotencyKey?: string,
): Promise<PostOut> {
  return request<PostOut>(`/api/v1/sites/${encodeURIComponent(siteId)}/posts`, {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

/**
 * Правка поста. Под объектом, а не отдельным адресом: видимость поста даёт объект, и пост
 * чужого объекта не правится даже по своему опознавателю.
 */
export function patchPost(siteId: string, postId: string, body: PostPatch): Promise<PostOut> {
  return request<PostOut>(
    `/api/v1/sites/${encodeURIComponent(siteId)}/posts/${encodeURIComponent(postId)}`,
    { method: 'PATCH', body },
  );
}

export function getNorm(
  siteId: string,
  year: number,
  month: number,
  signal?: AbortSignal,
): Promise<NormOut> {
  return request<NormOut>(
    `/api/v1/sites/${encodeURIComponent(siteId)}/norm${queryString({ year, month })}`,
    { signal },
  );
}

/** Ручной норматив на месяц. `hours: null` снимает переопределение и возвращает расчётный. */
export function setNormOverride(
  siteId: string,
  year: number,
  month: number,
  body: NormOverrideIn,
): Promise<NormOut> {
  return request<NormOut>(
    `/api/v1/sites/${encodeURIComponent(siteId)}/norm-override${queryString({ year, month })}`,
    { method: 'PUT', body },
  );
}

export function getStaffing(siteId: string, signal?: AbortSignal): Promise<StaffingOut> {
  return request<StaffingOut>(`/api/v1/sites/${encodeURIComponent(siteId)}/staffing`, { signal });
}
